import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Plus, Mail, ExternalLink, ClipboardList, Calendar,
  User, Briefcase, ChevronRight, CheckCircle2, XCircle,
  Clock, Ban, CircleDot, MapPin, Star, Link2, Pencil,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import type { Application, EmailThread } from '@core/types';
import { applicationService } from '@core/supabase/services/applicationService';
import { interviewEventService, type InterviewEvent } from '@core/supabase/services/interviewEventService';
import { emailService } from '@core/supabase/services/emailService';
import { RecordHeader } from '@shared/components/ui/RecordHeader';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { ApplicationStatusChip } from '@shared/components/ui/StatusChip';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { InterviewMonthCalendar, type MonthCalendarEvent } from './InterviewMonthCalendar';
import { InterviewWizardModal } from './InterviewWizardModal';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

/* ─── Constants ────────────────────────────────────────────────── */
const BRAND = '#8B80F9';

/** Shared dt/dd — colors + wrap; ledger from global `.ggj-attr-grid` */
const PARAM_DT = 'vscode-text-secondary pr-2 min-w-0';
const PARAM_DD = 'vscode-text-primary min-w-0 break-words';

const ROUND_STATUS_COLOR: Record<string, string> = {
  scheduled:        '#f59e0b',
  conducted:        '#22c55e',
  cancelled:        '#6b7280',
  no_show:          '#ef4444',
  draft:            '#94a3b8',
  awaiting_booking: '#94a3b8',
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  scheduled:        <Clock         className="w-4 h-4 text-amber-400" />,
  conducted:        <CheckCircle2  className="w-4 h-4 text-emerald-400" />,
  cancelled:        <Ban           className="w-4 h-4 vscode-text-tertiary" />,
  no_show:          <XCircle       className="w-4 h-4 text-red-400" />,
  draft:            <CircleDot     className="w-4 h-4 vscode-text-tertiary" />,
  awaiting_booking: <Clock         className="w-4 h-4 text-sky-400" />,
};

const VALID_TABS = ['overview', 'candidate', 'hiring', 'interview', 'email', 'parameters'] as const;
type DetailTab = (typeof VALID_TABS)[number];

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'overview',   label: 'Overview'      },
  { id: 'candidate',  label: 'Candidate'     },
  { id: 'hiring',     label: 'Hiring Detail' },
  { id: 'interview',  label: 'Interviews'    },
  { id: 'email',      label: 'Email'         },
  { id: 'parameters', label: 'Parameters'    },
];

/* ─── Helpers ──────────────────────────────────────────────────── */
function fmtDate(iso: string | null | undefined) {
  return formatDateTimeGmt(iso);
}
function fmtDateLong(iso: string | null | undefined) {
  return formatDateTimeGmt(iso);
}
function toLocalDayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function refLabel(id: string) {
  return `APP-${id.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}

function recordInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

const AttrRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <>
    <dt className={PARAM_DT}>{label}</dt>
    <dd className={PARAM_DD}>{children}</dd>
  </>
);

function skillChips(skills: string[] | null | undefined, _primary: boolean) {
  if (!skills?.length) return <p className="text-xs vscode-text-tertiary">None listed</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {skills.map((s) => (
        <span
          key={s}
          className="rounded-md border border-[var(--vscode-border-primary)] bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_55%,transparent)] px-2 py-0.5 text-xs font-medium vscode-text-secondary"
        >
          {s}
        </span>
      ))}
    </div>
  );
}

/* ─── Component ────────────────────────────────────────────────── */
export function InterviewApplicationDetailPage() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as DetailTab | null;
  const [activeTab, setActiveTab] = useState<DetailTab>(
    VALID_TABS.includes(tabFromUrl ?? ('' as DetailTab)) ? tabFromUrl! : 'overview'
  );
  const [app, setApp] = useState<Application | null>(null);
  const [events, setEvents] = useState<InterviewEvent[]>([]);
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [monthAnchor, setMonthAnchor] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardScheduleDayKey, setWizardScheduleDayKey] = useState<string | null>(null);

  const setTab = (t: DetailTab) => {
    setActiveTab(t);
    setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true });
  };

  useEffect(() => {
    if (VALID_TABS.includes(tabFromUrl ?? ('' as DetailTab))) setActiveTab(tabFromUrl!);
  }, [tabFromUrl]);

  const load = useCallback(async () => {
    if (!applicationId) return;
    const [row, evs, mails] = await Promise.all([
      applicationService.getById(applicationId),
      interviewEventService.listByApplication(applicationId),
      emailService.listByApplication(applicationId),
    ]);
    setApp(row);
    setEvents(evs);
    setThreads(mails);
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId || !user?.orgId) return;
    setLoading(true);
    load()
      .catch(() => { setApp(null); setEvents([]); setThreads([]); })
      .finally(() => setLoading(false));
  }, [applicationId, user?.orgId, load]);

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => {
      const ta = a.scheduled_at ? new Date(a.scheduled_at).getTime() : new Date(a.created_at).getTime();
      const tb = b.scheduled_at ? new Date(b.scheduled_at).getTime() : new Date(b.created_at).getTime();
      return ta - tb;
    }),
    [events]
  );

  const monthEvents: MonthCalendarEvent[] = useMemo(() => {
    const start = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
    const end = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0, 23, 59, 59, 999);
    return events
      .filter((r) => r.scheduled_at)
      .filter((r) => { const t = new Date(r.scheduled_at!); return t >= start && t <= end; })
      .map((r) => ({ id: r.id, scheduled_at: r.scheduled_at!, label: `R${r.round}` }));
  }, [events, monthAnchor]);

  const nextInterview = useMemo(() => {
    const now = Date.now();
    return sortedEvents.find((e) => e.scheduled_at && new Date(e.scheduled_at).getTime() >= now) ?? null;
  }, [sortedEvents]);

  if (!applicationId) return <p className="vscode-text-secondary p-4">Missing application ID.</p>;
  if (loading) return <FullPageSpinner />;
  if (!app || app.org_id !== user?.orgId) return <p className="vscode-text-secondary p-4">Application not found.</p>;

  const cand = app.candidate;
  const job = app.job;
  const client = app.client;
  const prepHref = cand?.id && job?.id
    ? `/interviews/prep?candidateId=${cand.id}&jobId=${job.id}`
    : '/interviews/prep';

  const conductedCount  = events.filter((e) => e.status === 'conducted').length;
  const scheduledCount  = events.filter((e) => e.status === 'scheduled').length;
  const cancelledCount  = events.filter((e) => e.status === 'cancelled').length;
  const noShowCount     = events.filter((e) => e.status === 'no_show').length;

  /* ── Shared sidebar panels ── */
  const schedulingPulse = (
    <CandidateCollapsiblePanel storageKey={`iapp-${applicationId}-sched`} title="Scheduling Pulse" defaultOpen>
      <div className="space-y-2.5 px-1 text-xs">
        <div>
          <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1.5 font-semibold">Pipeline</p>
          <ApplicationStatusChip status={app.status} />
        </div>
        <div className="pt-2 border-t vscode-border space-y-1.5">
          <div className="flex justify-between">
            <span className="vscode-text-tertiary">Total rounds</span>
            <span className="vscode-text-primary font-medium">{events.length || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="vscode-text-tertiary">Conducted</span>
            <span className="font-medium" style={{ color: '#22c55e' }}>{conductedCount || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="vscode-text-tertiary">Upcoming</span>
            <span className="font-medium" style={{ color: '#f59e0b' }}>{scheduledCount || '—'}</span>
          </div>
        </div>
        {nextInterview?.scheduled_at && (
          <div className="pt-2 border-t vscode-border">
            <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1 font-semibold">Next up</p>
            <p className="vscode-text-primary font-semibold text-xs leading-snug">{fmtDateLong(nextInterview.scheduled_at)}</p>
            <p className="vscode-text-tertiary text-[10px] mt-0.5">Round {nextInterview.round} · {nextInterview.interviewer_name ?? 'Interviewer TBD'}</p>
          </div>
        )}
      </div>
    </CandidateCollapsiblePanel>
  );

  const quickLinks = (
    <CandidateCollapsiblePanel storageKey={`iapp-${applicationId}-qlinks`} title="Quick Links" defaultOpen>
      <div className="space-y-0.5 px-1">
        {([
          { to: `/applications/${app.id}`,                                          label: 'Full Application'      },
          { to: prepHref,                                                            label: 'Prep & Scorecards'     },
          cand?.id && { to: `/candidates/${cand.id}`,                               label: cand.full_name ?? 'Candidate Profile' },
          job?.id  && { to: `/jobs/${job.id}`,                                      label: job.title ?? 'Job'      },
          client?.id && { to: `/clients/${client.id}`,                              label: client.name ?? 'Client' },
          cand?.id && { to: `/interviews/candidate/${cand.id}?applicationId=${app.id}`, label: 'Interview Workspace' },
          { to: `/emails/compose?applicationId=${app.id}`,                          label: 'Compose Email'         },
        ] as const).filter(Boolean).map((l) => {
          if (!l) return null;
          const link = l as { to: string; label: string };
          return (
            <Link key={link.to} to={link.to}
              className="flex items-center justify-between gap-2 py-1.5 text-sm text-brand-400 hover:text-brand-300">
              <span className="truncate">{link.label}</span>
              <ChevronRight className="w-3.5 h-3.5 shrink-0" />
            </Link>
          );
        })}
      </div>
    </CandidateCollapsiblePanel>
  );

  /* ── Page ── */
  return (
    <div className="w-full min-w-0 mx-auto max-w-none">
      <RecordHeader
        avatarInitials={recordInitials(cand?.full_name ?? '')}
        title={cand?.full_name ?? 'Application'}
        meta={[job?.title, client?.name].filter(Boolean).join(' · ') || undefined}
        breadcrumb={[
          { label: 'Interviews', href: '/interviews' },
          { label: cand?.full_name ?? 'Application' },
        ]}
      />

      {/* ── Tab bar — ggj-cand-tablist pattern, matches Requisitions ── */}
      <nav className="ggj-cand-tablist mb-4" aria-label="Interview application sections">
        {TABS.map(({ id: tid, label }) => {
          const count = (tid === 'interview' && events.length > 0) ? events.length
            : (tid === 'email' && threads.length > 0) ? threads.length
            : null;
          return (
            <button key={tid} type="button" onClick={() => setTab(tid)}
              className={`ggj-cand-tab ${activeTab === tid ? 'ggj-cand-tab--active' : ''}`}>
              {label}{count ? ` (${count})` : ''}
            </button>
          );
        })}
      </nav>

      {/* ══════════════════════════════════ OVERVIEW ══════════════════════════════════ */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">

          <div className="ggj-param-group overflow-hidden">
            {/* Toolbar */}
            <div className="ggj-profile-toolbar">
              <Link to={`/applications/${app.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                <ExternalLink className="w-3.5 h-3.5" /> Full Application
              </Link>
              <Link to={prepHref} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5" /> Prep &amp; Scorecards
              </Link>
              <Link to={`/emails/compose?applicationId=${app.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Compose Email
              </Link>
            </div>

            {/* Candidate context header */}
            <div className="ggj-profile-section-h">
              {(cand?.full_name ?? 'CANDIDATE').toUpperCase()}
            </div>
            <div className="ggj-profile-section-body">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
                  style={{ background: `${BRAND}20`, color: BRAND, border: `1px solid ${BRAND}35` }}>
                  {(cand?.full_name?.[0] ?? '?').toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-bold vscode-text-primary truncate">{cand?.full_name ?? '—'}</p>
                  {[cand?.current_title, cand?.current_company].filter(Boolean).join(' @ ') && (
                    <p className="text-sm vscode-text-secondary mt-0.5">
                      {[cand?.current_title, cand?.current_company].filter(Boolean).join(' @ ')}
                    </p>
                  )}
                  {cand?.location && (
                    <p className="text-xs vscode-text-tertiary mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />{cand.location}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <ApplicationStatusChip status={app.status} />
                </div>
              </div>
              {/* Stats row */}
              <div className="flex flex-wrap gap-5 mt-3 pt-3 border-t vscode-border">
                <div className="text-center">
                  <p className="text-lg font-bold vscode-text-primary">{events.length}</p>
                  <p className="text-xs vscode-text-tertiary">Rounds</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold" style={{ color: '#22c55e' }}>{conductedCount}</p>
                  <p className="text-xs vscode-text-tertiary">Conducted</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold" style={{ color: '#f59e0b' }}>{scheduledCount}</p>
                  <p className="text-xs vscode-text-tertiary">Upcoming</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold vscode-text-primary">{refLabel(app.id)}</p>
                  <p className="text-xs vscode-text-tertiary">Reference</p>
                </div>
                {threads.length > 0 && (
                  <div className="text-center">
                    <p className="text-lg font-bold vscode-text-primary">{threads.length}</p>
                    <p className="text-xs vscode-text-tertiary">Emails</p>
                  </div>
                )}
              </div>
            </div>

            {/* Application Status */}
            <div className="ggj-profile-section-h">Application Status</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <AttrRow label="Pipeline stage">
                  <span className="capitalize">{app.status.replace(/_/g, ' ')}</span>
                </AttrRow>
                <AttrRow label="Submitted">{fmtDateLong(app.submitted_at)}</AttrRow>
                <AttrRow label="Last updated">{fmtDateLong(app.updated_at)}</AttrRow>
                <AttrRow label="Assigned to">
                  {app.assigned_user?.display_name ?? app.assigned_user?.email ?? '—'}
                </AttrRow>
                <AttrRow label="Job">
                  {job?.id
                    ? <Link to={`/jobs/${job.id}`} className="hover:underline" style={{ color: BRAND }}>{job.title}</Link>
                    : '—'}
                </AttrRow>
                <AttrRow label="Client">
                  {client?.id
                    ? <Link to={`/clients/${client.id}`} className="hover:underline" style={{ color: BRAND }}>{client.name}</Link>
                    : '—'}
                </AttrRow>
                {app.fitment_score != null && (
                  <AttrRow label="Fitment score">
                    <span className="font-semibold" style={{ color: BRAND }}>{app.fitment_score}%</span>
                  </AttrRow>
                )}
              </dl>
            </div>

            {/* Interview Overview */}
            <div className="ggj-profile-section-h">Interview Overview</div>
            <div className="ggj-profile-section-body">
              {events.length === 0 ? (
                <p className="text-sm vscode-text-tertiary">No interview rounds scheduled yet.</p>
              ) : (
                <dl className="ggj-attr-grid">
                  <AttrRow label="Next scheduled">
                    {nextInterview?.scheduled_at ? fmtDateLong(nextInterview.scheduled_at) : '—'}
                  </AttrRow>
                  <AttrRow label="Next round">{nextInterview ? `R${nextInterview.round}` : '—'}</AttrRow>
                  <AttrRow label="Lead interviewer">{nextInterview?.interviewer_name ?? '—'}</AttrRow>
                  <AttrRow label="Location / link">{nextInterview?.location ?? '—'}</AttrRow>
                  <AttrRow label="Total rounds">{String(events.length)}</AttrRow>
                  <AttrRow label="Completed">{String(conductedCount)}</AttrRow>
                </dl>
              )}
            </div>

            {app.internal_notes && (
              <>
                <div className="ggj-profile-section-h">Internal Notes</div>
                <div className="ggj-profile-section-body">
                  <p className="text-sm vscode-text-secondary leading-relaxed whitespace-pre-wrap">{app.internal_notes}</p>
                </div>
              </>
            )}
          </div>

          <aside className="ggj-rail-aside flex flex-col gap-3 lg:sticky lg:top-4">
            {schedulingPulse}
            {quickLinks}
          </aside>
        </div>
      )}

      {/* ══════════════════════════════════ CANDIDATE ══════════════════════════════════ */}
      {activeTab === 'candidate' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">

          <div className="ggj-param-group overflow-hidden">
            <div className="ggj-profile-toolbar">
              {cand?.id && (
                <Link to={`/candidates/${cand.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5" /> Open Candidate Record
                </Link>
              )}
              {cand?.email && (
                <a href={`mailto:${cand.email}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> Email Candidate
                </a>
              )}
            </div>

            {/* Personal Details */}
            <div className="ggj-profile-section-h">Personal Details</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <AttrRow label="Full name">{cand?.full_name ?? '—'}</AttrRow>
                <AttrRow label="Email">
                  {cand?.email
                    ? <a href={`mailto:${cand.email}`} className="hover:underline" style={{ color: BRAND }}>{cand.email}</a>
                    : '—'}
                </AttrRow>
                <AttrRow label="Phone">
                  {cand?.phone
                    ? <a href={`tel:${cand.phone}`} className="hover:underline" style={{ color: BRAND }}>{cand.phone}</a>
                    : '—'}
                </AttrRow>
                <AttrRow label="Location">{cand?.location ?? '—'}</AttrRow>
                {cand?.linkedin_url && (
                  <AttrRow label="LinkedIn">
                    <a href={cand.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-1" style={{ color: BRAND }}>
                      <Link2 className="w-3 h-3" /> View profile
                    </a>
                  </AttrRow>
                )}
                {cand?.recruiter_rating != null && (
                  <AttrRow label="Recruiter rating">
                    <span className="inline-flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-amber-400" />
                      <span className="font-medium">{cand.recruiter_rating} / 5</span>
                    </span>
                  </AttrRow>
                )}
              </dl>
            </div>

            {/* Professional Background */}
            <div className="ggj-profile-section-h">Professional Background</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <AttrRow label="Current title">{cand?.current_title ?? '—'}</AttrRow>
                <AttrRow label="Current company">{cand?.current_company ?? '—'}</AttrRow>
                <AttrRow label="Years experience">
                  {cand?.years_exp != null ? `${cand.years_exp} years` : '—'}
                </AttrRow>
                <AttrRow label="Source channel">{cand?.source_channel ?? cand?.source ?? '—'}</AttrRow>
              </dl>
            </div>

            {/* Education */}
            {cand?.education?.length ? (
              <>
                <div className="ggj-profile-section-h">Education</div>
                <div className="ggj-profile-section-body space-y-2">
                  {cand.education.map((e, i) => (
                    <div key={i} className="text-sm">
                      <p className="vscode-text-primary font-medium">{e.degree}</p>
                      <p className="vscode-text-secondary text-xs">{e.institution}{e.year ? ` · ${e.year}` : ''}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {/* Skills */}
            <div className="ggj-profile-section-h">Skills</div>
            <div className="ggj-profile-section-body">
              {skillChips(cand?.parsed_skills, true)}
            </div>

            {cand?.tags?.length ? (
              <>
                <div className="ggj-profile-section-h">Tags</div>
                <div className="ggj-profile-section-body">
                  {skillChips(cand.tags, false)}
                </div>
              </>
            ) : null}

            {/* Notes */}
            {cand?.notes && (
              <>
                <div className="ggj-profile-section-h">Recruiter Notes</div>
                <div className="ggj-profile-section-body">
                  <p className="text-sm vscode-text-secondary leading-relaxed whitespace-pre-wrap">{cand.notes}</p>
                </div>
              </>
            )}

            {/* Application — This Role */}
            <div className="ggj-profile-section-h">Application — This Role</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <AttrRow label="Job applied for">
                  {job?.id
                    ? <Link to={`/jobs/${job.id}`} className="hover:underline" style={{ color: BRAND }}>{job.title}</Link>
                    : '—'}
                </AttrRow>
                <AttrRow label="Client">
                  {client?.id
                    ? <Link to={`/clients/${client.id}`} className="hover:underline" style={{ color: BRAND }}>{client.name}</Link>
                    : '—'}
                </AttrRow>
                <AttrRow label="Pipeline stage">
                  <span className="capitalize">{app.status.replace(/_/g, ' ')}</span>
                </AttrRow>
                <AttrRow label="Submitted">{fmtDateLong(app.submitted_at)}</AttrRow>
                {app.fitment_score != null && (
                  <AttrRow label="Fitment score">
                    <span className="font-semibold" style={{ color: BRAND }}>{app.fitment_score}%</span>
                  </AttrRow>
                )}
              </dl>
            </div>
          </div>

          <aside className="ggj-rail-aside flex flex-col gap-3 lg:sticky lg:top-4">
            {schedulingPulse}
            {quickLinks}
          </aside>
        </div>
      )}

      {/* ══════════════════════════════════ HIRING DETAIL ══════════════════════════════════ */}
      {activeTab === 'hiring' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">

          <div className="ggj-param-group overflow-hidden">
            <div className="ggj-profile-toolbar">
              {job?.id && (
                <Link to={`/jobs/${job.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5" /> Open Job Record
                </Link>
              )}
              {client?.id && (
                <Link to={`/clients/${client.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5" /> Open Client Record
                </Link>
              )}
            </div>

            {/* Job Details */}
            <div className="ggj-profile-section-h">Job Details</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <AttrRow label="Job title">{job?.title ?? '—'}</AttrRow>
                <AttrRow label="Location">{job?.location ?? '—'}</AttrRow>
                <AttrRow label="Experience">
                  {job?.experience_min != null || job?.experience_max != null
                    ? `${job?.experience_min ?? 0}–${job?.experience_max ?? '?'} years`
                    : '—'}
                </AttrRow>
                <AttrRow label="Job status">
                  <span className="capitalize">{job?.status?.replace(/_/g, ' ') ?? '—'}</span>
                </AttrRow>
                <AttrRow label="Publish status">
                  <span className="capitalize">{job?.publish_status?.replace(/_/g, ' ') ?? '—'}</span>
                </AttrRow>
                {job?.opened_at && <AttrRow label="Opened">{fmtDate(job.opened_at)}</AttrRow>}
                {job?.filled_at && <AttrRow label="Filled">{fmtDate(job.filled_at)}</AttrRow>}
              </dl>
            </div>

            {/* Required Skills */}
            <div className="ggj-profile-section-h">Required Skills</div>
            <div className="ggj-profile-section-body">
              {skillChips(job?.skills_required, true)}
            </div>

            {/* Job Description */}
            {job?.description && (
              <>
                <div className="ggj-profile-section-h">Job Description</div>
                <div className="ggj-profile-section-body">
                  <p className="text-sm vscode-text-secondary leading-relaxed whitespace-pre-wrap line-clamp-6">{job.description}</p>
                </div>
              </>
            )}

            {/* Client / Employer */}
            <div className="ggj-profile-section-h">Client / Employer</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <AttrRow label="Company name">
                  {client?.id
                    ? <Link to={`/clients/${client.id}`} className="hover:underline" style={{ color: BRAND }}>{client.name}</Link>
                    : '—'}
                </AttrRow>
                <AttrRow label="Industry">{client?.industry ?? '—'}</AttrRow>
                <AttrRow label="Contact name">{client?.contact_name ?? '—'}</AttrRow>
                <AttrRow label="Contact email">
                  {client?.contact_email
                    ? <a href={`mailto:${client.contact_email}`} className="hover:underline" style={{ color: BRAND }}>{client.contact_email}</a>
                    : '—'}
                </AttrRow>
                <AttrRow label="Contact phone">{client?.contact_phone ?? '—'}</AttrRow>
                <AttrRow label="Domain">{client?.domain ?? '—'}</AttrRow>
              </dl>
            </div>

            {/* Application Assignment */}
            <div className="ggj-profile-section-h">Application Assignment</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <AttrRow label="Pipeline stage">
                  <span className="capitalize">{app.status.replace(/_/g, ' ')}</span>
                </AttrRow>
                <AttrRow label="Assigned recruiter">
                  {app.assigned_user?.display_name ?? app.assigned_user?.email ?? 'Unassigned'}
                </AttrRow>
                <AttrRow label="Submitted">{fmtDateLong(app.submitted_at)}</AttrRow>
                <AttrRow label="Last updated">{fmtDateLong(app.updated_at)}</AttrRow>
                {app.placement_fee != null && (
                  <AttrRow label="Placement fee">₹{app.placement_fee.toLocaleString()}</AttrRow>
                )}
                {app.placement_date && (
                  <AttrRow label="Placement date">{fmtDate(app.placement_date)}</AttrRow>
                )}
              </dl>
            </div>
          </div>

          <aside className="ggj-rail-aside flex flex-col gap-3 lg:sticky lg:top-4">
            {schedulingPulse}
            {quickLinks}
          </aside>
        </div>
      )}

      {/* ══════════════════════════════════ INTERVIEWS ══════════════════════════════════ */}
      {activeTab === 'interview' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(360px,32%)] gap-3 lg:gap-5 items-start">

          <div className="ggj-param-group overflow-hidden">
            <div className="ggj-profile-toolbar">
              <button type="button" className="ggj-toolbar-btn inline-flex items-center gap-1.5"
                onClick={() => { setWizardScheduleDayKey(null); setWizardOpen(true); }}>
                <Plus className="w-3.5 h-3.5" /> New Interview
              </button>
              <Link to={prepHref} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5" /> Prep Workspace
              </Link>
              {cand?.id && (
                <Link to={`/interviews/candidate/${cand.id}?applicationId=${app.id}`}
                  className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5" /> Interview Workspace
                </Link>
              )}
            </div>

            <div className="ggj-profile-section-h">Interview Rounds</div>
            <div className="ggj-profile-section-body">
              {sortedEvents.length === 0 ? (
                <p className="text-sm vscode-text-tertiary">
                  No rounds scheduled yet — use New Interview or click a day on the calendar.
                </p>
              ) : (
                <div className="rounded-xl border vscode-border overflow-hidden">
                  <table className="ggj-data-table w-full">
                    <thead>
                      <tr className="border-b vscode-border">
                        <th className="px-3 py-2.5 text-left text-xs vscode-text-tertiary uppercase tracking-wide">Round</th>
                        <th className="px-3 py-2.5 text-left text-xs vscode-text-tertiary uppercase tracking-wide">Date &amp; Time</th>
                        <th className="px-3 py-2.5 text-left text-xs vscode-text-tertiary uppercase tracking-wide hidden sm:table-cell">Interviewer</th>
                        <th className="px-3 py-2.5 text-left text-xs vscode-text-tertiary uppercase tracking-wide hidden md:table-cell">Location</th>
                        <th className="px-3 py-2.5 text-left text-xs vscode-text-tertiary uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedEvents.map((ev) => {
                        const statusColor = ROUND_STATUS_COLOR[ev.status] ?? '#94a3b8';
                        const isSelected = selectedEventId === ev.id;
                        return (
                          <tr key={ev.id}
                            onClick={() => setSelectedEventId(isSelected ? null : ev.id)}
                            className={`cursor-pointer transition-colors ${isSelected ? 'bg-brand-500/10' : 'hover:vscode-bg-hover'}`}>
                            <td className="px-3 py-2.5 text-sm font-bold vscode-text-primary">R{ev.round}</td>
                            <td className="px-3 py-2.5 text-sm vscode-text-primary whitespace-nowrap">
                              {ev.scheduled_at
                                ? formatDateTimeGmt(ev.scheduled_at)
                                : <span className="vscode-text-tertiary text-xs">TBD</span>}
                            </td>
                            <td className="px-3 py-2.5 text-sm vscode-text-secondary hidden sm:table-cell">
                              {ev.interviewer_name ?? '—'}
                            </td>
                            <td className="px-3 py-2.5 text-xs vscode-text-secondary max-w-[140px] truncate hidden md:table-cell" title={ev.location ?? ''}>
                              {ev.location ?? '—'}
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize"
                                style={{ background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}35` }}>
                                {STATUS_ICON[ev.status]}
                                {ev.status.replace(/_/g, ' ')}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Expanded event detail */}
            {selectedEventId && (() => {
              const sel = events.find((e) => e.id === selectedEventId);
              if (!sel) return null;
              const statusColor = ROUND_STATUS_COLOR[sel.status] ?? '#94a3b8';
              return (
                <>
                  <div className="ggj-profile-section-h">Round {sel.round} — Detail</div>
                  <div className="ggj-profile-section-body">
                    <dl className="ggj-attr-grid">
                      <AttrRow label="Status">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
                          style={{ background: `${statusColor}18`, color: statusColor }}>
                          {STATUS_ICON[sel.status]} {sel.status.replace(/_/g, ' ')}
                        </span>
                      </AttrRow>
                      <AttrRow label="Scheduled">{fmtDateLong(sel.scheduled_at)}</AttrRow>
                      <AttrRow label="Duration">{sel.duration_minutes ? `${sel.duration_minutes} min` : '—'}</AttrRow>
                      <AttrRow label="Location / link">{sel.location ?? '—'}</AttrRow>
                      <AttrRow label="Lead interviewer">{sel.interviewer_name ?? '—'}</AttrRow>
                      <AttrRow label="Interviewer email">
                        {sel.interviewer_email
                          ? <a href={`mailto:${sel.interviewer_email}`} className="hover:underline" style={{ color: BRAND }}>{sel.interviewer_email}</a>
                          : '—'}
                      </AttrRow>
                      <AttrRow label="Invite sent">{fmtDateLong(sel.invite_sent_at)}</AttrRow>
                    </dl>
                    {sel.notes && (
                      <div className="mt-3 pt-3 border-t vscode-border">
                        <p className="text-xs vscode-text-tertiary mb-1.5">Agenda / Notes</p>
                        <p className="text-sm vscode-text-secondary leading-relaxed whitespace-pre-wrap">{sel.notes}</p>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Right: Calendar + pulse sidebar */}
          <aside className="ggj-rail-aside flex flex-col gap-3 lg:sticky lg:top-4">
            <CandidateCollapsiblePanel storageKey={`iapp-${applicationId}-cal`} title="Schedule Calendar" defaultOpen>
              <div className="px-0.5">
                <InterviewMonthCalendar
                  monthAnchor={monthAnchor}
                  onMonthAnchorChange={setMonthAnchor}
                  events={monthEvents}
                  selectedId={selectedEventId}
                  onSelectEvent={(id) => setSelectedEventId(id)}
                  onDayClick={(day) => { setWizardScheduleDayKey(toLocalDayKey(day)); setWizardOpen(true); }}
                />
                <p className="text-[10px] vscode-text-tertiary mt-2 px-1">
                  Click a day to schedule an interview for that date.
                </p>
              </div>
            </CandidateCollapsiblePanel>
            {schedulingPulse}
          </aside>
        </div>
      )}

      {/* ══════════════════════════════════ EMAIL ══════════════════════════════════ */}
      {activeTab === 'email' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">

          <div className="ggj-param-group overflow-hidden">
            <div className="ggj-profile-toolbar">
              <Link to={`/emails/compose?applicationId=${app.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> New Email
              </Link>
              {cand?.email && (
                <a href={`mailto:${cand.email}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Email Candidate
                </a>
              )}
              {client?.contact_email && (
                <a href={`mailto:${client.contact_email}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5" /> Email Client
                </a>
              )}
            </div>

            <div className="ggj-profile-section-h">Email Threads</div>
            <div className="ggj-profile-section-body">
              {threads.length === 0 ? (
                <p className="text-sm vscode-text-tertiary">No email threads linked to this application yet.</p>
              ) : (
                <ul className="divide-y divide-[var(--vscode-border-primary)] rounded-xl border vscode-border overflow-hidden">
                  {threads.map((th) => (
                    <li key={th.id} className="vscode-bg-secondary hover:vscode-bg-hover transition-colors">
                      <Link to={`/emails/thread/${th.id}`} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm vscode-text-primary font-medium truncate">{th.subject ?? '(No subject)'}</p>
                          <div className="flex items-center gap-3 mt-0.5">
                            <p className="text-xs vscode-text-tertiary">
                              {th.sent_at ? formatDateTimeGmt(th.sent_at) : '—'}
                            </p>
                            {th.opened_at && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                                style={{ background: '#22c55e15', color: '#22c55e' }}>
                                Opened
                              </span>
                            )}
                            {th.cta_response && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium capitalize"
                                style={{ background: `${BRAND}15`, color: BRAND }}>
                                {th.cta_response}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 vscode-text-tertiary shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <aside className="ggj-rail-aside flex flex-col gap-3 lg:sticky lg:top-4">
            <CandidateCollapsiblePanel storageKey={`iapp-${applicationId}-email-sum`} title="Email Summary" defaultOpen>
              <div className="space-y-1.5 px-1 text-xs">
                <div className="flex justify-between">
                  <span className="vscode-text-tertiary">Total threads</span>
                  <span className="vscode-text-primary font-medium">{threads.length || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="vscode-text-tertiary">Opened</span>
                  <span className="vscode-text-primary font-medium">{threads.filter((t) => t.opened_at).length || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="vscode-text-tertiary">CTA responses</span>
                  <span className="vscode-text-primary font-medium">{threads.filter((t) => t.cta_response).length || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="vscode-text-tertiary">Latest sent</span>
                  <span className="vscode-text-primary font-medium">{threads[0]?.sent_at ? fmtDate(threads[0].sent_at) : '—'}</span>
                </div>
              </div>
            </CandidateCollapsiblePanel>
            {quickLinks}
          </aside>
        </div>
      )}

      {/* ══════════════════════════════════ PARAMETERS ══════════════════════════════════ */}
      {activeTab === 'parameters' && (
        <div className="ggj-param-group overflow-hidden">
          <div className="ggj-profile-toolbar">
            <span className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">Parameters</span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Link
                to={`/applications/${applicationId}?edit=1`}
                className="ggj-toolbar-btn inline-flex items-center gap-1.5 text-brand-500 font-medium"
              >
                <Pencil className="w-3.5 h-3.5 shrink-0" aria-hidden />
                Edit
              </Link>
            </div>
          </div>
          <div className="px-5 py-2 border-b vscode-border">
            <p className="text-xs vscode-text-tertiary">Read-only audit fields for this interview application record. Use Edit to change notes on the application record.</p>
          </div>

          {/* Object — name, description, type (matches candidate Parameters) */}
            <div className="border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
            <div className="ggj-profile-section-h">Object</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <dt className={PARAM_DT}>Name</dt>
                <dd className={`${PARAM_DD} font-medium`}>
                  {[cand?.full_name, job?.title].filter(Boolean).join(' — ') || '—'}
                </dd>
                <dt className={PARAM_DT}>Object type</dt>
                <dd className={`${PARAM_DD} vscode-text-secondary`}>Interview application (read-only)</dd>
                <dt className={PARAM_DT}>Description</dt>
                <dd className={`${PARAM_DD} min-w-0 lg:col-span-3`}>
                  <textarea
                    readOnly
                    tabIndex={-1}
                    rows={5}
                    value={app.internal_notes?.trim() ? app.internal_notes : ''}
                    placeholder="—"
                    aria-label="Description"
                    className="ggj-param-readonly-textarea"
                  />
                </dd>
              </dl>
            </div>
          </div>

          {/* Candidate */}
          <div className="ggj-profile-section-h">Candidate</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Full name</dt>
              <dd className={PARAM_DD}>{cand?.full_name ?? '—'}</dd>
              <dt className={PARAM_DT}>Email</dt>
              <dd className={PARAM_DD}>{cand?.email ?? '—'}</dd>
              <dt className={PARAM_DT}>Phone</dt>
              <dd className={PARAM_DD}>{cand?.phone ?? '—'}</dd>
              <dt className={PARAM_DT}>Location</dt>
              <dd className={PARAM_DD}>{cand?.location ?? '—'}</dd>
              <dt className={PARAM_DT}>Current title</dt>
              <dd className={PARAM_DD}>{cand?.current_title ?? '—'}</dd>
              <dt className={PARAM_DT}>Current company</dt>
              <dd className={PARAM_DD}>{cand?.current_company ?? '—'}</dd>
              <dt className={PARAM_DT}>Experience</dt>
              <dd className={PARAM_DD}>{cand?.years_exp != null ? `${cand.years_exp} years` : '—'}</dd>
              <dt className={PARAM_DT}>Source</dt>
              <dd className={PARAM_DD}>{cand?.source_channel ?? cand?.source ?? '—'}</dd>
              <dt className={PARAM_DT}>Recruiter rating</dt>
              <dd className={PARAM_DD}>{cand?.recruiter_rating != null ? `${cand.recruiter_rating} / 5` : '—'}</dd>
            </dl>
          </div>

          {/* Application */}
          <div className="ggj-profile-section-h">Application</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Pipeline stage</dt>
              <dd className={PARAM_DD + ' capitalize'}>{app.status.replace(/_/g, ' ')}</dd>
              <dt className={PARAM_DT}>Submitted</dt>
              <dd className={PARAM_DD}>{fmtDateLong(app.submitted_at)}</dd>
              <dt className={PARAM_DT}>Last updated</dt>
              <dd className={PARAM_DD}>{fmtDateLong(app.updated_at)}</dd>
              <dt className={PARAM_DT}>Assigned to</dt>
              <dd className={PARAM_DD}>{app.assigned_user?.display_name ?? app.assigned_user?.email ?? '—'}</dd>
              <dt className={PARAM_DT}>Fitment score</dt>
              <dd className={PARAM_DD}>{app.fitment_score != null ? `${app.fitment_score}%` : '—'}</dd>
              {app.rejection_reason_code && <>
                <dt className={PARAM_DT}>Rejection reason</dt>
                <dd className={PARAM_DD}>{app.rejection_reason_code.replace(/_/g, ' ')}</dd>
              </>}
              {app.placement_fee != null && <>
                <dt className={PARAM_DT}>Placement fee</dt>
                <dd className={PARAM_DD}>₹{app.placement_fee.toLocaleString()}</dd>
              </>}
              {app.placement_date && <>
                <dt className={PARAM_DT}>Placement date</dt>
                <dd className={PARAM_DD}>{fmtDate(app.placement_date)}</dd>
              </>}
            </dl>
          </div>

          {/* Interview Rounds */}
          <div className="ggj-profile-section-h">Interview Rounds</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Total rounds</dt>
              <dd className={PARAM_DD}>{events.length}</dd>
              <dt className={PARAM_DT}>Conducted</dt>
              <dd className={PARAM_DD}>{conductedCount}</dd>
              <dt className={PARAM_DT}>Upcoming (scheduled)</dt>
              <dd className={PARAM_DD}>{scheduledCount}</dd>
              <dt className={PARAM_DT}>Cancelled</dt>
              <dd className={PARAM_DD}>{cancelledCount}</dd>
              <dt className={PARAM_DT}>No-shows</dt>
              <dd className={PARAM_DD}>{noShowCount}</dd>
            </dl>
            {sortedEvents.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs vscode-text-tertiary uppercase tracking-wide font-medium">Round detail</p>
                {sortedEvents.map((ev) => {
                  const statusColor = ROUND_STATUS_COLOR[ev.status] ?? '#94a3b8';
                  return (
                    <div key={ev.id} className="flex items-start gap-3 py-1.5 border-b vscode-border last:border-0">
                      <span className="font-bold vscode-text-primary text-sm w-6 shrink-0">R{ev.round}</span>
                      <div className="flex-1 min-w-0 text-xs space-y-0.5">
                        <p className="vscode-text-secondary">{fmtDateLong(ev.scheduled_at)}</p>
                        {ev.interviewer_name && <p className="vscode-text-tertiary truncate">Lead: {ev.interviewer_name}</p>}
                        {ev.location && <p className="vscode-text-tertiary truncate">{ev.location}</p>}
                        <p className="font-mono text-[10px] vscode-text-tertiary truncate">{ev.id}</p>
                      </div>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium capitalize shrink-0"
                        style={{ background: `${statusColor}18`, color: statusColor }}>
                        {ev.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Job */}
          <div className="ggj-profile-section-h">Job</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Title</dt>
              <dd className={PARAM_DD}>
                {job?.id
                  ? <Link to={`/jobs/${job.id}`} className="hover:underline" style={{ color: BRAND }}>{job.title}</Link>
                  : '—'}
              </dd>
              <dt className={PARAM_DT}>Location</dt>
              <dd className={PARAM_DD}>{job?.location ?? '—'}</dd>
              <dt className={PARAM_DT}>Status</dt>
              <dd className={PARAM_DD + ' capitalize'}>{job?.status?.replace(/_/g, ' ') ?? '—'}</dd>
              <dt className={PARAM_DT}>Experience range</dt>
              <dd className={PARAM_DD}>
                {job?.experience_min != null || job?.experience_max != null
                  ? `${job?.experience_min ?? 0}–${job?.experience_max ?? '?'} years`
                  : '—'}
              </dd>
              <dt className={PARAM_DT}>Required skills</dt>
              <dd className={PARAM_DD}>{job?.skills_required?.join(', ') || '—'}</dd>
            </dl>
          </div>

          {/* Client */}
          <div className="ggj-profile-section-h">Client</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Company name</dt>
              <dd className={PARAM_DD}>
                {client?.id
                  ? <Link to={`/clients/${client.id}`} className="hover:underline" style={{ color: BRAND }}>{client.name}</Link>
                  : '—'}
              </dd>
              <dt className={PARAM_DT}>Industry</dt>
              <dd className={PARAM_DD}>{client?.industry ?? '—'}</dd>
              <dt className={PARAM_DT}>Contact name</dt>
              <dd className={PARAM_DD}>{client?.contact_name ?? '—'}</dd>
              <dt className={PARAM_DT}>Contact email</dt>
              <dd className={PARAM_DD}>{client?.contact_email ?? '—'}</dd>
              <dt className={PARAM_DT}>Contact phone</dt>
              <dd className={PARAM_DD}>{client?.contact_phone ?? '—'}</dd>
              <dt className={PARAM_DT}>Domain</dt>
              <dd className={PARAM_DD}>{client?.domain ?? '—'}</dd>
            </dl>
          </div>

          {/* System Attributes */}
          <div className="ggj-profile-section-h">System Attributes</div>
          <div className="ggj-profile-section-body">
            <p className="text-xs vscode-text-tertiary mb-3">Immutable audit fields — cannot be edited.</p>
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Application ID</dt>
              <dd className={PARAM_DD + ' font-mono text-xs truncate'} title={app.id}>{app.id}</dd>
              <dt className={PARAM_DT}>Reference</dt>
              <dd className={PARAM_DD + ' font-mono text-xs'}>{refLabel(app.id)}</dd>
              <dt className={PARAM_DT}>Candidate ID</dt>
              <dd className={PARAM_DD + ' font-mono text-xs truncate'} title={cand?.id ?? ''}>{cand?.id ?? '—'}</dd>
              <dt className={PARAM_DT}>Job ID</dt>
              <dd className={PARAM_DD + ' font-mono text-xs truncate'} title={job?.id ?? ''}>{job?.id ?? '—'}</dd>
              <dt className={PARAM_DT}>Client ID</dt>
              <dd className={PARAM_DD + ' font-mono text-xs truncate'} title={client?.id ?? ''}>{client?.id ?? '—'}</dd>
              <dt className={PARAM_DT}>Organisation ID</dt>
              <dd className={PARAM_DD + ' font-mono text-xs truncate'} title={app.org_id}>{app.org_id}</dd>
              <dt className={PARAM_DT}>Created / Submitted</dt>
              <dd className={PARAM_DD + ' text-xs'}>{fmtDateLong(app.submitted_at)}</dd>
              <dt className={PARAM_DT}>Modified when</dt>
              <dd className={PARAM_DD + ' text-xs'}>{fmtDateLong(app.updated_at)}</dd>
              <dt className={PARAM_DT}>Email threads</dt>
              <dd className={PARAM_DD}>{threads.length}</dd>
              <dt className={PARAM_DT}>Interview rounds</dt>
              <dd className={PARAM_DD}>{events.length}</dd>
            </dl>
          </div>
        </div>
      )}

      {/* ── Wizard modal ── */}
      <InterviewWizardModal
        open={wizardOpen}
        onClose={() => { setWizardOpen(false); setWizardScheduleDayKey(null); }}
        orgId={user!.orgId}
        orgName={user!.orgName}
        recruiterName={user!.name}
        applications={[app]}
        scheduleDayKey={wizardScheduleDayKey}
        initialApplicationId={applicationId}
        onCreated={() => {
          void load();
          setWizardScheduleDayKey(null);
        }}
      />
    </div>
  );
}
